/**
 * useInactivityTimer
 * 
 * Attaches global event listeners (click, touchstart, keydown, mousemove, scroll)
 * to detect user activity. If the user does nothing for `timeoutMs` milliseconds,
 * `onTimeout` is called.
 *
 * Usage:
 *   useInactivityTimer({ timeoutMs: 30_000, onTimeout: () => endSession(), enabled: true });
 *
 * The hook cleans up all listeners automatically on unmount.
 */
import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = [
  'click',
  'touchstart',
  'keydown',
  'mousemove',
  'scroll',
  'pointerdown',
];

export function useInactivityTimer({
  timeoutMs = 30_000,
  onTimeout,
  enabled = true,
  warningMs = 5_000, // Optional: call onWarning when this many ms remain
  onWarning,
}) {
  const timerRef    = useRef(null);
  const warningRef  = useRef(null);
  const onTimeoutCb = useRef(onTimeout);
  const onWarningCb = useRef(onWarning);

  // Keep callback refs fresh without re-registering listeners on every render
  useEffect(() => { onTimeoutCb.current  = onTimeout;  }, [onTimeout]);
  useEffect(() => { onWarningCb.current  = onWarning;  }, [onWarning]);

  const clearTimers = useCallback(() => {
    if (timerRef.current)   clearTimeout(timerRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    timerRef.current   = null;
    warningRef.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();

    if (onWarningCb.current && warningMs > 0 && warningMs < timeoutMs) {
      warningRef.current = setTimeout(() => {
        onWarningCb.current?.();
      }, timeoutMs - warningMs);
    }

    timerRef.current = setTimeout(() => {
      onTimeoutCb.current?.();
    }, timeoutMs);
  }, [timeoutMs, warningMs, clearTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Start the timer immediately when enabled
    resetTimer();

    // Attach activity listeners
    const handleActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      );
    };
  }, [enabled, resetTimer, clearTimers]);

  return { resetTimer };
}
